<template>
  <section class="hero-section" aria-label="פרקים מומלצים">
    <transition name="hero-fade">
    <div
      v-if="loaded"
      class="hero-card"
      :key="heroItem.id"
      @mouseenter="hover = true"
      @mouseleave="hover = false"
    >
      <!-- Background image -->
      <div
        class="hero-card__bg"
        :style="heroItem.pic_hero_parsed ? 'background-image:url(' + heroItem.pic_hero_parsed + ')' : ''"
        aria-hidden="true"
      ></div>

      <!-- Promo video (if present) -->
      <div class="hero-card__video" v-if="heroItem.video_promo" aria-hidden="true">
        <video-bg
          ref="videobg_elem"
          :muted="muted"
          :sources="[heroItem.video_promo]"
          :img="heroItem.pic_hero_parsed"
        ></video-bg>
      </div>

      <!-- Gradient overlays -->
      <div class="hero-card__grad-h" aria-hidden="true"></div>
      <div class="hero-card__grad-v" aria-hidden="true"></div>
      <div class="hero-card__grad-top" aria-hidden="true"></div>

      <!-- Content block -->
      <div class="hero-card__content">
        <!-- Brand label -->
        <div class="hero-card__label" v-if="heroItem.sticker > 0">
          <span v-text="$store.state.stickers[heroItem.sticker].name"></span>
        </div>
        <div class="hero-card__label" v-else>סדרת מקור · טוב</div>

        <!-- Title -->
        <component
          :is="heroItem.restricted ? 'span' : 'router-link'"
          :to="heroItem.restricted ? undefined : heroItem.url"
          class="hero-card__title-link"
        >
          <h1 class="hero-card__title">{{ heroItem.title }}</h1>
        </component>

        <!-- Metadata row -->
        <div class="hero-card__meta" v-if="heroItem.year || heroItem.type">
          <span class="hero-card__meta-item">{{ heroItem.type == 'series' ? 'סדרה' : 'סרט' }}</span>
          <span v-if="heroItem.main_category" class="hero-card__meta-item">{{ heroItem.main_category.name }}</span>
          <span v-if="heroItem.seasons && heroItem.seasons.length > 1" class="hero-card__meta-item">{{ heroItem.seasons.length }} עונות</span>
          <span v-if="heroItem.year" class="hero-card__meta-item">{{ heroItem.year }}</span>
        </div>

        <!-- Description -->
        <p class="hero-card__desc" v-if="heroItem.description">{{ heroItem.description }}</p>

        <!-- CTA row -->
        <div class="hero-card__cta" v-if="!heroItem.restricted && !(heroItem.main_category && heroItem.main_category.id == 195)">
          <ui-button variant="white" size="lg" @click="$router.push(heroItem.url)">
            <template #icon-right>
              <svg width="18" height="18" style="rotate:180deg;" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>
            </template>
            צפה עכשיו
          </ui-button>
          <ui-button variant="secondary" size="lg" @click="add_to_fav(heroItem.id)">
            <template #icon-left>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
            </template>
            הוסף לרשימה
          </ui-button>
          <!-- <ui-icon-button
            variant="glass"
            size="lg"
            aria-label="מידע נוסף"
            @click="$router.push('/info/' + heroItem.id)"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
          </ui-icon-button> -->
        </div>

        <!-- Restricted label -->
        <div class="hero-card__restricted" v-if="heroItem.restricted" role="status">מוגבל</div>

      </div>

      <!-- Mute toggle (video only) — absolute bottom-end of hero card -->
      <button
        v-if="heroItem.video_promo"
        class="hero-card__mute"
        :aria-label="muted ? 'הפעל שמע' : 'השתק'"
        @click="muted = !muted"
      >
        <svg v-if="muted" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.5 12A4.5 4.5 0 0 0 14 7.97v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3 3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06A8.99 8.99 0 0 0 17.73 18L19 19.27 20.27 18l-9-9L4.27 3zM12 4 9.91 6.09 12 8.18V4z"/></svg>
        <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>
      </button>

      <!-- Slide dots -->
      <div class="hero-card__dots" v-if="promotedComp.length > 1" role="tablist" aria-label="בחר שקופית">
        <button
          v-for="item in promotedComp"
          :key="item.id"
          class="hero-card__dot"
          :class="{ 'hero-card__dot--active': item.id === heroItem.id }"
          role="tab"
          :aria-selected="item.id === heroItem.id ? 'true' : 'false'"
          :aria-label="'שקופית ' + (parseInt(item.indexSlide) + 1)"
          @click="goToSlide(item)"
        ></button>
      </div>

      <!-- Nav arrows -->
      <button class="hero-card__nav hero-card__nav--prev" aria-label="שקופית קודמת" @click="prevSlide">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>
     </button>
      <button class="hero-card__nav hero-card__nav--next" aria-label="שקופית הבאה" @click="nextSlide">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>
      </button>
    </div>
    </transition>
  </section>
</template>

<script>
import UiButton from './ui/Button.vue';
import UiIconButton from './ui/IconButton.vue';

export default {
  components: { UiButton, UiIconButton },
  props: ['promoted'],

  data() {
    return {
      heroItem: {},
      muted: true,
      loaded: false,
      timer: null,
      curItem: 0,
      hover: false,
    };
  },

  computed: {
    promotedComp() {
      const ret = [];
      for (let i in this.promoted) {
        const src = this.promoted[i];
        const cloned = Object.assign({}, src);
        cloned.indexSlide = i;
        if (!cloned.video_promo) {
          cloned.bgImg = cloned.pic_hero_parsed;
        }
        cloned.url =
          cloned.restricted === true
            ? ''
            : (cloned.type == 'live' ? '/livePage/' : '/info/') + cloned.id;
        ret[i] = cloned;
      }
      return ret;
    },
  },

  methods: {
    goToSlide(item) {
      this.curItem = parseInt(item.indexSlide);
      this.heroItem = item;
    },
    nextSlide() {
      const next = this.curItem >= this.promotedComp.length - 1 ? 0 : this.curItem + 1;
      this.curItem = next;
      this.heroItem = this.promotedComp[next];
    },
    prevSlide() {
      const prev = this.curItem <= 0 ? this.promotedComp.length - 1 : this.curItem - 1;
      this.curItem = prev;
      this.heroItem = this.promotedComp[prev];
    },
    startSlide() {
      const reduceMotion =
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion) return;
      this.timer = setInterval(() => {
        if (!this.hover) this.nextSlide();
      }, 4000);
    },
  },

  mounted() {
    if (this.promotedComp.length > 0) {
      this.heroItem = this.promotedComp[0];
    }
    this.loaded = true;
    this.startSlide();
  },

  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
  },
};
</script>

<style scoped lang="scss">
/* ── Hero section wrapper ── */
.hero-section {
  /* Overlap under the 68px sticky header — full bleed, no side padding */
  margin-top: -68px;
  padding: 0;
  position: relative;
  height: 65vh;
  min-height: 420px;
  max-height: 620px;
}

/* ── Hero card ── */
.hero-card {
  position: absolute;
  inset: 0;
  border-radius: 0;
  overflow: hidden;
  background: var(--color-surface1);
}

/* Background image */
.hero-card__bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  /* Anchor near the top: hero images are wider than the card on desktop, so
     `cover` crops vertically. Centering split that crop between top and
     bottom and cut off the subject's head; the bottom is already hidden
     under the gradient + content block, so lose the crop there instead. */
  background-position: center 12%;
}

/* Promo video layer */
.hero-card__video {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
}

/* Gradients */
.hero-card__grad-h {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(90deg, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.5) 50%, transparent 100%);
}
[dir="rtl"] .hero-card__grad-h {
  background: linear-gradient(-90deg, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.5) 50%, transparent 100%);
}

.hero-card__grad-v {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(0deg, rgba(0,0,0,0.6) 0%, transparent 50%);
}

/* Top gradient — keeps transparent header nav readable over bright images */
.hero-card__grad-top {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(0,0,0,0.55) 0%, transparent 30%);
}

/* Content block */
.hero-card__content {
  position: absolute;
  inset-inline-start: 40px;
  bottom: 40px;
  inset-inline-end: 40px;
  max-width: 540px;
  z-index: 2;
}

/* Brand label */
.hero-card__label {
  display: inline-block;
  padding: 4px 8px;
  background: var(--color-accent);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  border-radius: 2px;
  margin-block-end: 14px;
  font-family: var(--font-sans);
  text-transform: uppercase;
}

/* Title link — reset */
.hero-card__title-link {
  display: block;
  text-decoration: none;
  color: inherit;
}

/* Title */
.hero-card__title {
  font-size: 44px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -1.2px;
  color: var(--color-text);
  margin: 0 0 14px;
  font-family: var(--font-hebrew);
}

/* Meta row */
.hero-card__meta {
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 13px;
  color: var(--color-textMuted);
  font-family: var(--font-mono);
  margin-block-end: 14px;
  flex-wrap: wrap;
}
.hero-card__meta-item + .hero-card__meta-item::before {
  content: '·';
  margin-inline-end: 6px;
}

/* Description */
.hero-card__desc {
  font-size: 15px;
  color: var(--color-text);
  line-height: 1.5;
  opacity: 0.92;
  margin: 0 0 22px;
  font-family: var(--font-hebrew);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* CTA row */
.hero-card__cta {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

/* Restricted label */
.hero-card__restricted {
  display: inline-block;
  background: var(--color-danger);
  color: #fff;
  padding: 3px 8px;
  font-size: 13px;
  border-radius: 2px;
  margin-block-end: 14px;
}

/* Mute button — anchored to bottom-end of hero card */
.hero-card__mute {
  position: absolute;
  bottom: var(--space-6);
  inset-inline-end: var(--space-6);
  z-index: 3;
  background: rgba(255,255,255,0.08);
  border: none;
  border-radius: var(--radius-sm);
  width: 44px;
  height: 44px;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.hero-card__mute:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

/* Slide dots */
.hero-card__dots {
  position: absolute;
  bottom: 16px;
  inset-inline-start: 0;
  inset-inline-end: 0;
  display: flex;
  justify-content: center;
  gap: 6px;
  align-items: center;
  z-index: 3;
}

.hero-card__dot {
  width: 6px;
  height: 3px;
  border-radius: 2px;
  background: var(--color-surface4);
  border: none;
  cursor: pointer;
  padding: 0;
  transition: width var(--duration-base) var(--easing-default),
              background var(--duration-base) var(--easing-default);
  /* Extend hit area */
  position: relative;
}
.hero-card__dot::before {
  content: '';
  position: absolute;
  inset: -8px -4px;
}
.hero-card__dot--active {
  width: 24px;
  background: var(--color-text);
}
.hero-card__dot:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

/* Nav arrows */
.hero-card__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  background: rgba(255,255,255,0.06);
  border: none;
  border-radius: var(--radius-sm);
  width: 36px;
  height: 80px;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background var(--duration-fast) var(--easing-default);
}
.hero-card__nav:hover {
  background: rgba(255,255,255,0.12);
}
.hero-card__nav:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}
.hero-card__nav--prev {
  inset-inline-start: 8px;
}
.hero-card__nav--next {
  inset-inline-end: 8px;
}

/* Slide crossfade transition — both layers stack absolutely so they don't render side-by-side mid-swap */
.hero-fade-enter-active,
.hero-fade-leave-active {
  transition: opacity 500ms ease;
}
.hero-fade-leave-active {
  /* Pin leaving slide on top while incoming fades in beneath; prevents 2-image stack */
  z-index: 1;
}
.hero-fade-enter {
  opacity: 0;
}
.hero-fade-leave-to {
  opacity: 0;
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .hero-card__dot,
  .hero-card__nav,
  .hero-fade-enter-active,
  .hero-fade-leave-active {
    transition: none;
  }
}

/* Mobile */
@media (max-width: 640px) {
  .hero-section {
    height: 62vh;
    min-height: 340px;
    max-height: 520px;
  }
  .hero-card__content {
    inset-inline-start: 20px;
    inset-inline-end: 20px;
    bottom: 60px;
  }
  .hero-card__title {
    font-size: 28px;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;

  }
  .hero-card__desc {
    display: none;
  }
}
</style>
